<script setup lang="ts">
import { defineAsyncComponent, computed, useSafeI18nNumber } from '#imports'
import type { RouteLocationRaw } from '#vue-router'

const YscIconsHeart = defineAsyncComponent(() => import('./YscIconsHeart.vue'))
const YscIconsHeartSolid = defineAsyncComponent(() => import('./YscIconsHeartSolid.vue'))
const YscIconsStarSolid = defineAsyncComponent(() => import('./YscIconsStarSolid.vue'))

interface Props {
  camper: any
  currency: {
    currency: string
    currencyDisplay: 'symbol'
    style: 'currency'
  }
  /** can be relative path or complete url prefixed by `//` */
  camperUrl?: RouteLocationRaw
  vehicleType: string
  titleTag?: 'h2' | 'h3'
  showPrice?: boolean
  showWishlist?: boolean
  showDiscount?: boolean
  showLink?: boolean
  showReviews?: boolean
  isInWishlist?: boolean
  showPreviewPrice?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  titleTag: 'h2',
  showPrice: false,
  showWishlist: true,
  showDiscount: false,
  showLink: true,
  showReviews: false,
  isInWishlist: false,
  showPreviewPrice: false,
  camperUrl: undefined,
})

defineEmits<{
  'wishlist-open': [number]
}>()

const travelers = computed(() => Math.min(...[props.camper.vehicle_seatbelts, props.camper.vehicle_beds].filter(v => !Number.isNaN(v))))
const price = computed(() => props.camper.final_booking_price ?? props.camper.original_booking_price ?? props.camper.price_per_day)
const priceFormat = computed(() => ({
  ...props.currency,
  style: 'currency',
  currencyDisplay: 'symbol',
  minimumFractionDigits: 0,
  maximumFractionDigits: 0,
}))

const { safeI18nNumber } = useSafeI18nNumber({
  extraSentryData: () => ({ camper: props.camper, currency: props.currency }),
  sentryTags: { fingerprint: 'yscCamperCardNumberFormat' },
})
</script>

<template>
  <article class="relative flex w-full flex-col overflow-hidden rounded">
    <div class="relative overflow-hidden rounded">
      <div
        v-if="props.showDiscount && props.camper.discounts_types.length"
        class="absolute left-2 top-2 z-10 flex flex-col items-baseline space-y-1"
      >
        <span class="rounded-xs bg-white px-1.5 py-1 text-xs font-semibold uppercase text-pink-500">
          {{ $t('camper_card.discount_special_offer') }}
        </span>
      </div>

      <button
        v-if="props.showWishlist"
        :aria-label="$t('commons.add-to-wishlist')"
        class="focus:ring-peacock-300 absolute right-1.5 top-1.5 z-30 p-1.5 focus:rounded-sm focus:outline-none focus:ring-2"
        name="wishlist"
        @click.prevent="$emit('wishlist-open', props.camper.id)"
      >
        <YscIconsHeartSolid
          v-if="props.isInWishlist"
          class="relative top-px h-6 w-6 text-pink-500"
        />
        <YscIconsHeart
          v-else
          class="relative top-px h-6 w-6 fill-black/40 text-white"
        />
      </button>

      <NuxtLink
        :to="camperUrl"
        target="_blank"
        class="focus-visible:ring-peacock-300 block border-0 focus-visible:rounded-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2"
      >
        <span
          v-if="props.showLink"
          class="absolute inset-0 z-[-1]"
          aria-hidden="true"
        />
        <slot name="slideshow" />
      </NuxtLink>
    </div>

    <div class="relative px-1.5 py-3">
      <NuxtLink
        :to="props.camperUrl"
        target="_blank"
        class="focus-visible:ring-peacock-300 block border-0 focus-visible:rounded-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-4"
      >
        <span
          class="absolute inset-0 z-10"
          aria-hidden="true"
        />
        <component
          :is="props.titleTag"
          class="truncate text-xl font-semibold"
        >
          {{ props.vehicleType }}
        </component>
      </NuxtLink>

      <div class="flex items-end justify-between">
        <div class="text-left">
          <p class="text-sm text-gray-500">
            {{ props.camper.vehicle_location_city }}
          </p>
          <p class="text-sm text-gray-500">
            {{ $t('commons.travelers_plural', { count_number: travelers }, travelers) }}
          </p>
          <template v-if="showReviews">
            <div
              v-if="props.camper.review_count > 0"
              class="mt-1 flex items-baseline space-x-1"
            >
              <YscIconsStarSolid class="h-4 w-4 place-self-center text-pink-500" />
              <span class="font-semibold text-black">
                {{
                  $n(props.camper.display_review_average, {
                    minimumFractionDigits: 1,
                    style: 'decimal',
                  })
                }}
              </span>
              <span class="text-gray-500"> ({{ $n(props.camper.review_count) }}) </span>
              <span
                v-if="props.camper.is_ambassador"
                class="text-xs font-semibold text-yellow-700"
              >
                {{ $t('commons.best_owner') }}
              </span>
            </div>

            <span
              v-else-if="props.camper.is_new"
              class="rounded-xs mt-2 inline-block bg-gray-100 px-1.5 py-0.5 text-xs font-semibold uppercase leading-tight text-gray-500"
            >
              {{ $t('commons.new') }}
            </span>

            <span
              v-else
              class="rounded-xs mt-2 inline-block bg-gray-100 px-1.5 py-0.5 text-xs font-semibold uppercase leading-tight text-gray-500"
            >
              {{ $t('commons.no_reviews') }}
            </span>
          </template>
        </div>

        <div
          v-if="props.showPrice && price"
          class="whitespace-nowrap text-right"
        >
          <span
            v-if="!props.camper.original_booking_price && !props.camper.final_booking_price"
            class="block text-sm text-gray-500"
          >
            {{ $t('camper_card.starting_price') }}
          </span>
          <span
            v-if="props.showPreviewPrice && props.camper.original_booking_price"
            class="block text-sm text-pink-500 line-through"
          >
            {{ safeI18nNumber(props.camper.original_booking_price, priceFormat) }}
          </span>
          <div class="flex items-center space-x-2">
            <YscIconsFlashSolid
              v-if="props.camper.instant_booking_activated"
              class="h-5 w-5 text-orange-400"
            />
            <span class="text-2xl font-semibold">
              {{ safeI18nNumber(price, priceFormat) }}
            </span>
          </div>
        </div>
      </div>

      <slot name="footer-block" />
    </div>
  </article>
</template>
